/**
 * HOOK: useCrewsForContact
 * Hook optimisé pour charger uniquement les équipages associés à un contact spécifique
 *
 * ⚡ Optimisation: Ne charge QUE les crews du contact via crew_members
 * 🎯 Usage: Dans ContactDetailPage au lieu de charger TOUS les crews
 *
 * @example
 * const { crews, loading, refresh } = useCrewsForContact(contactId);
 *
 * // Au lieu de:
 * // const { getCrewsByContactId } = useCrews(); // ❌ Charge TOUS les crews d'abord
 */

import { useEffect, useState, useCallback } from 'react';
import { useSupabase } from './useSupabase';
import type { Crew } from '../types/crew';
import { monitorQuery } from '../lib/supabase-monitor';

interface UseCrewsForContactReturn {
  crews: Crew[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook optimisé pour récupérer les équipages d'un contact spécifique
 * Utilise une requête directe via la table de jointure crew_members
 */
export function useCrewsForContact(contactId: string | undefined): UseCrewsForContactReturn {
  const supabase = useSupabase();
  const [crews, setCrews] = useState<Crew[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCrews = useCallback(async () => {
    if (!contactId) {
      setCrews([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 📊 MONITORED: Récupération crews pour un contact spécifique
      const { data, error: fetchError } = await monitorQuery(
        'crew_members',
        'select',
        `contact=${contactId} with all relations`,
        (() =>
          supabase
            .from('crew_members')
            .select(
              `
          crew:crews(
            *,
            product:products(id, name),
            vehicle:vehicles(id, registration),
            association:associations(id, name),
            discount:discounts(id, name, amount),
            members:crew_members(
              contact:contacts(id, first_name, last_name, email, phone)
            )
          )
        `
            )
            .eq('contact_id', contactId)) as any,
        'useCrewsForContact.fetchCrews'
      );

      if (fetchError) throw fetchError;

      // Extraire et transformer les crews
      const transformedCrews = ((data || []) as any[])
        .map((item: any) => item.crew)
        .filter(Boolean)
        .map((raw: any) => ({
          id: raw.id,
          name: raw.name,
          crew_number: raw.crew_number || null,
          status: raw.status,
          probability: raw.probability,
          budget: raw.budget || null,
          assigned_to: raw.assigned_to || null,
          product_id: raw.product_id || null,
          vehicle_id: raw.vehicle_id || null,
          association_id: raw.association_id || null,
          discount_id: raw.discount_id || null,
          financing_type: raw.financing_type,
          has_derogation: raw.has_derogation,
          derogation_pending: raw.derogation_pending ?? false,
          organizes_pvd: raw.organizes_pvd,
          has_vehicle: raw.has_vehicle ?? false,
          report: raw.report || null,
          notes: raw.notes || null,
          created_at: raw.created_at,
          updated_at: raw.updated_at,
          // Relations
          product: raw.product || null,
          vehicle: raw.vehicle || null,
          association: raw.association || null,
          discount: raw.discount || null,
          // Aplatir la structure des membres
          members: (raw.members?.map((m: any) => m.contact).filter(Boolean) || []) as any,
          assigned_user: null,
        }));

      setCrews(transformedCrews);
    } catch (err) {
      console.error('[useCrewsForContact] Error:', err);
      setError(err as Error);
      setCrews([]);
    } finally {
      setLoading(false);
    }
  }, [contactId, supabase]);

  useEffect(() => {
    fetchCrews();
  }, [fetchCrews]);

  return {
    crews,
    loading,
    error,
    refresh: fetchCrews,
  };
}
